/* eslint-disable react-refresh/only-export-components -- hook + provider component are tightly coupled */
import {
	createContext,
	type ReactNode,
	use,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

/* ── Types ──────────────────────────────────────────── */

interface Toast {
	id: number;
	message: string;
	/** 'add' = checkmark accent, 'remove' = muted, 'info' = neutral */
	variant: "add" | "remove" | "info";
	/** Internal flag for exit animation */
	exiting?: boolean;
}

interface ToastContextValue {
	show: (message: string, variant?: Toast["variant"]) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/* ── Hook ───────────────────────────────────────────── */

export function useBuilderToast(): ToastContextValue {
	const ctx = use(ToastContext);
	if (!ctx) {
		// Graceful fallback — callers outside the provider just no-op
		return { show: () => {} };
	}
	return ctx;
}

/* ── Provider + Renderer ────────────────────────────── */

const TOAST_DURATION = 2000;
const EXIT_DURATION = 200;

let nextId = 0;

export function BuilderToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
		new Map(),
	);

	const scheduleRemoval = useCallback((id: number) => {
		// Start exit animation
		const exitTimer = setTimeout(() => {
			setToasts((prev) =>
				prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
			);
			// After exit animation completes, remove from DOM
			const removeTimer = setTimeout(() => {
				setToasts((prev) => prev.filter((t) => t.id !== id));
				timersRef.current.delete(id);
			}, EXIT_DURATION);
			timersRef.current.set(id + 100000, removeTimer);
		}, TOAST_DURATION);
		timersRef.current.set(id, exitTimer);
	}, []);

	const show = useCallback(
		(message: string, variant: Toast["variant"] = "info") => {
			const id = ++nextId;
			setToasts((prev) => [...prev.slice(-2), { id, message, variant }]);
			scheduleRemoval(id);
		},
		[scheduleRemoval],
	);

	// `show` is already stable; memoize the wrapper too, or every consumer
	// effect that lists it in its deps re-runs on each provider render.
	const contextValue = useMemo(() => ({ show }), [show]);

	// Cleanup timers on unmount
	useEffect(() => {
		const timers = timersRef.current;
		return () => {
			timers.forEach((timer) => clearTimeout(timer));
		};
	}, []);

	return (
		<ToastContext.Provider value={contextValue}>
			{children}
			{toasts.length > 0 && (
				<div
					className="builder-toast-container"
					aria-live="polite"
					aria-atomic="false"
				>
					{toasts.map((toast) => (
						<div
							key={toast.id}
							className={`builder-toast builder-toast--${toast.variant}${toast.exiting ? " builder-toast--exit" : ""}`}
							role="status"
						>
							<span className="builder-toast__icon" aria-hidden="true">
								{toast.variant === "add" && "✓"}
								{toast.variant === "remove" && "✕"}
								{toast.variant === "info" && "●"}
							</span>
							<span className="builder-toast__message">{toast.message}</span>
						</div>
					))}
				</div>
			)}
		</ToastContext.Provider>
	);
}
